import { useLoadFeatureAccessInsights } from '@ms/yammer-hooks/dist/featureAccessInsights';
import { useLoadMultiTenantOrganization } from '@ms/yammer-hooks/dist/multiTenantOrganization';
import { useSendPresenceState } from '@ms/yammer-hooks/dist/userPresence';
import { useCurrentLocale } from '@ms/yammer-i18n-react';
import { AppContextOptions, AppContextProvider } from '@ms/yammer-libs-app-context';
import { NotificationsCountSubscription } from '@ms/yammer-libs-realtime-subscriptions';
import AppDocumentLanguage from '@ms/yammer-web-components/dist/app/AppDocumentLanguage';
import FavoritesLimitReachedDialog from '@ms/yammer-web-components/dist/group/FavoritesLimitReachedDialog';
import { LinkRenderingOptions, LinkRenderingOptionsProvider } from '@ms/yammer-web-components/dist/link/YammerLink';
import MediaLightbox from '@ms/yammer-web-components/dist/messageAttachments/MediaLightbox';
import RealtimeClient from '@ms/yammer-web-components/dist/realtime/RealtimeClient';
import SessionContextProviderWithHooks from '@ms/yammer-web-components/dist/session/SessionContextProviderWithHooks';
import Toast from '@ms/yammer-web-components/dist/toast/Toast';
import UsagePolicyDialog from '@ms/yammer-web-components/dist/usagePolicy/UsagePolicyDialog';
import AppContainer from '@ms/yammer-web-core-ui/dist/components/AppContainer';
import { usePrepareIcons } from '@ms/yammer-web-core-ui/dist/util/icons';
import { UIFeaturesProvider } from '@ms/yammer-web-ui-features/dist/features';
import TeamsClientProvider from 'components/TeamsClientProvider';
import {
  useDeepLinkPath,
  useIsMobileClient,
  useIsTeamsCached,
  useIsTeamsContrastTheme,
  useIsTeamsTab,
} from 'hooks/selectors';
import { useAppFeatureSettings } from 'hooks/useAppFeatureSettings';
import { useAppThemes } from 'hooks/useAppThemes';
import { useLoadUnseenNotifications } from 'hooks/useLoadUnseenNotifications';
import { useReportNotificationsCountEffect } from 'hooks/useReportNotificationsCountEffect';
import React, { FC, useMemo } from 'react';
import { isInVivaConnectionsApp } from 'vivaConnections';

import AppLayout from '../AppLayout';
import Routes from '../Routes';

const App: FC = () => {
  const themes = useAppThemes();
  const isMobileClient = useIsMobileClient();
  const isTeamsTab = useIsTeamsTab();
  const isTeamsContrastTheme = useIsTeamsContrastTheme();
  const features = useAppFeatureSettings();
  const locale = useCurrentLocale();
  const isCached = useIsTeamsCached();

  const deepLinkPath = useDeepLinkPath();
  const hasDeepLinkPath = !!deepLinkPath;
  const shouldDisableRealtime = hasDeepLinkPath || isCached;

  useLoadUnseenNotifications();
  useReportNotificationsCountEffect();
  useSendPresenceState();
  useLoadMultiTenantOrganization();
  useLoadFeatureAccessInsights({ vivaEngagePremiumOnly: true });

  const isInVivaConnections = isInVivaConnectionsApp();
  usePrepareIcons();

  const teamsClientContextOptions = useMemo(
    () => ({
      collapseContentSidebar: false,
      shouldHideHeaders: isTeamsTab || isInVivaConnections,
      shouldHideLeftSidebar: isTeamsTab,
      shouldHideSubHeader: isTeamsTab || isInVivaConnections,
      shouldShowStaticAppHeader: false,
    }),
    [isTeamsTab, isInVivaConnections]
  );

  const appContextOptions: Partial<AppContextOptions> = useMemo(
    () => ({
      enableCollapsiblePanelNav: !isTeamsTab,
      enableCommunityDiscoveryV2: !isTeamsTab && !isInVivaConnections,
      disableVideoPlayer: isCached,
    }),
    [isTeamsTab, isInVivaConnections, isCached]
  );

  const linkRenderingOptions: LinkRenderingOptions = useMemo(
    () => ({
      disableLivePersonaCard: isMobileClient,
      shouldHideDownloadAttachmentLink: isMobileClient,
      shouldHideLegacyPageLinks: isMobileClient,
      shouldHideDescriptionMoreInfoLink: isMobileClient,
      shouldOpenDiscoverCommunitiesLinkInNewWindow: false,
      shouldOpenGroupHeaderSettingsLinkInNewWindow: true,
      shouldOpenGroupInfoLinkInNewWindow: true,
      shouldOpenGroupFilesInLegacyPage: true,
      shouldOpenGroupFilesInNewWindow: true,
      shouldOpenGroupDeleteInNewWindow: true,
      isThreadStarterCopyButtonLinkExternal: true,
      shouldOpenEventLinkInNewWindow: isTeamsTab,
      shouldOpenGroupLinkInNewWindow: isTeamsTab,
      shouldOpenThreadLinkInNewWindow: isTeamsTab,
      shouldOpenTagLinkInNewWindow: isTeamsTab,
      shouldOpenViewConversationLinkInNewWindow: isTeamsTab,
      shouldOpenYammerLinkInNewWindow: isTeamsTab,
    }),
    [isMobileClient, isTeamsTab]
  );

  return (
    <AppContainer
      theme={themes.fluent8}
      fluent9Theme={themes.fluent9}
      emulateHighContrast={isTeamsContrastTheme}
      locale={locale}
    >
      <AppContextProvider mainColumnAriaRole="region" options={appContextOptions}>
        <UIFeaturesProvider features={features}>
          <SessionContextProviderWithHooks>
            <AppDocumentLanguage />
            <TeamsClientProvider options={teamsClientContextOptions}>
              <LinkRenderingOptionsProvider options={linkRenderingOptions}>
                <AppLayout>
                  <Routes />
                </AppLayout>
                <UsagePolicyDialog />
                <FavoritesLimitReachedDialog />
                <MediaLightbox />
                <Toast />
              </LinkRenderingOptionsProvider>
            </TeamsClientProvider>
            <NotificationsCountSubscription />
            <RealtimeClient isRealtimeDisabled={shouldDisableRealtime} />
          </SessionContextProviderWithHooks>
        </UIFeaturesProvider>
      </AppContextProvider>
    </AppContainer>
  );
};

export default App;
